import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, formatRelative } from "../lib/api";
import { Users, Plus, Send, X, Crown, LogOut } from "lucide-react";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { toast } from "sonner";

export default function GroupDMSection({ currentUser }) {
  const [groups, setGroups] = useState([]);
  const [activeGroup, setActiveGroup] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const bottomRef = useRef(null);

  const loadGroups = () =>
    api.get("/group-conversations").then((r) => setGroups(r.data)).catch(() => {});

  useEffect(() => { loadGroups(); }, []);

  // Auto-refresh hvert 10. sekund
  useEffect(() => {
    const t = setInterval(() => {
      loadGroups();
      if (activeGroup) loadMessages(activeGroup.id, false);
    }, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [activeGroup?.id]);

  const loadMessages = async (groupId, scroll = true) => {
    try {
      const r = await api.get(`/group-conversations/${groupId}/messages`);
      setMessages(r.data);
      if (scroll && bottomRef.current) {
        setTimeout(() => bottomRef.current?.scrollTo(0, bottomRef.current.scrollHeight), 50);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openGroup = (g) => {
    setActiveGroup(g);
    loadMessages(g.id);
  };

  const send = async () => {
    if (!body.trim() || !activeGroup) return;
    setSending(true);
    try {
      await api.post(`/group-conversations/${activeGroup.id}/messages`, { body });
      setBody("");
      await loadMessages(activeGroup.id);
      loadGroups();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const leaveGroup = async (groupId) => {
    if (!window.confirm("Forlat denne gruppen? Du kan ikke bli med igjen uten ny invitasjon.")) return;
    try {
      await api.delete(`/group-conversations/${groupId}/leave`);
      setActiveGroup(null);
      setMessages([]);
      loadGroups();
      toast.success("Du forlot gruppen");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="group-dm-section">
      <div className="flex items-center justify-between mb-4">
        <div className="font-meta text-[var(--cream-soft)]">
          <Users className="h-3.5 w-3.5 inline mr-1.5" />
          {groups.length} {groups.length === 1 ? "gruppe" : "grupper"}
        </div>
        <button onClick={() => setShowCreate(true)}
          className="btn-magenta inline-flex items-center gap-1.5 text-xs"
          data-testid="create-group-btn">
          <Plus className="h-3.5 w-3.5" /> Opprett gruppe
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-px bg-white/8 border border-white/8"
           style={{ height: "calc(100vh - 360px)", minHeight: "520px" }}>
        {/* Gruppe-liste */}
        <aside className="bg-[var(--noir)] overflow-y-auto" data-testid="groups-list">
          {groups.length === 0 ? (
            <div className="p-6 font-fraunces italic text-[var(--cream-soft)]">
              Du er ikke med i noen gruppe-samtaler enda.
            </div>
          ) : (
            <ul>
              {groups.map((g) => (
                <li key={g.id} className="border-b border-white/5">
                  <button onClick={() => openGroup(g)}
                    className={`w-full flex items-center gap-3 px-3 py-3 text-left transition ${
                      activeGroup?.id === g.id ? "bg-[var(--noir-elev)]" : "hover:bg-[var(--noir-elev)]/50"
                    }`}
                    data-testid={`group-item-${g.id}`}>
                    <div className="h-10 w-10 rounded-sm border border-white/10 bg-[var(--magenta)]/15 flex items-center justify-center shrink-0">
                      <Users className="h-4 w-4 text-[var(--magenta)]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-fraunces italic text-base text-[var(--cream)] truncate">{g.name}</span>
                        {(g.unread?.[currentUser?.id] || 0) > 0 && (
                          <span className="font-meta text-[10px] bg-[var(--magenta)] text-black px-1.5 py-0.5">
                            {g.unread[currentUser.id]}
                          </span>
                        )}
                      </div>
                      <div className="font-meta text-[10px] truncate">
                        {g.participants?.length || 0} medlemmer · {formatRelative(g.last_message_at)}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* Aktiv gruppe-chat */}
        <section className="bg-[var(--noir)] flex flex-col h-full min-h-0 overflow-hidden">
          {!activeGroup ? (
            <div className="flex-1 flex flex-col items-center justify-center p-12 text-center">
              <Users className="h-10 w-10 text-[var(--cream-faint)] mb-3" />
              <div className="font-fraunces italic text-2xl text-[var(--cream-soft)]">
                Velg en gruppe
              </div>
            </div>
          ) : (
            <>
              <header className="border-b border-white/10 px-6 py-4 flex items-center justify-between shrink-0">
                <div>
                  <div className="font-fraunces italic text-xl text-[var(--cream)]" data-testid="active-group-title">
                    {activeGroup.name}
                  </div>
                  <div className="font-meta mt-1 text-[10px] flex flex-wrap items-center gap-1.5">
                    {(activeGroup.participants || []).map((p) => (
                      <Link key={p.id} to={`/profile/${p.username}`}
                        className="hover:text-[var(--magenta)]">
                        {p.id === activeGroup.creator_id && <Crown className="h-2.5 w-2.5 inline mr-0.5 text-[var(--gold)]" />}
                        @{p.username}
                      </Link>
                    ))}
                  </div>
                </div>
                <button onClick={() => leaveGroup(activeGroup.id)}
                  className="btn-ghost text-xs inline-flex items-center gap-1.5"
                  data-testid="leave-group-btn">
                  <LogOut className="h-3 w-3" /> Forlat
                </button>
              </header>

              <div ref={bottomRef} className="flex-1 overflow-y-auto min-h-0 p-6 space-y-3" data-testid="group-messages">
                {messages.length === 0 ? (
                  <div className="text-center font-fraunces italic text-[var(--cream-soft)] mt-10">
                    Vær den første som skriver i gruppen.
                  </div>
                ) : (
                  messages.map((m) => {
                    const mine = m.sender_id === currentUser?.id;
                    return (
                      <div key={m.id} className={`flex items-start gap-2 ${mine ? "justify-end" : "justify-start"}`}>
                        {!mine && (
                          <Avatar className="h-7 w-7 rounded-sm border border-white/10 shrink-0">
                            <AvatarFallback className="bg-[var(--magenta)] text-black text-[10px] font-bold rounded-sm">
                              {(m.sender?.username || "?").slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                        )}
                        <div className={`max-w-[70%] ${mine ? "" : ""}`}>
                          {!mine && (
                            <div className="font-meta text-[10px] mb-0.5 text-[var(--cream-soft)]">
                              @{m.sender?.username}
                            </div>
                          )}
                          <div className={`px-4 py-2.5 ${
                            mine
                              ? "bg-[var(--magenta)]/12 border border-[var(--magenta)]/45 text-[var(--cream)] border-r-2"
                              : "bg-[var(--noir-elev)] text-[var(--cream)] border border-white/10"
                          }`}>
                            <p className="font-manrope text-sm leading-relaxed whitespace-pre-wrap">{m.body}</p>
                            <div className={`font-meta text-[9px] mt-1 ${mine ? "text-[var(--magenta)]" : "text-[var(--cream-faint)]"}`}>
                              {formatRelative(m.created_at)}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="border-t border-white/10 px-4 py-3 shrink-0 flex gap-2">
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Skriv en melding…"
                  rows={1}
                  className="input-editorial flex-1 resize-none"
                  data-testid="group-msg-input"
                />
                <button onClick={send} disabled={!body.trim() || sending}
                  className="btn-magenta px-4 inline-flex items-center"
                  data-testid="group-msg-send">
                  <Send className="h-3.5 w-3.5" />
                </button>
              </div>
            </>
          )}
        </section>
      </div>

      {showCreate && (
        <CreateGroupModal onClose={() => setShowCreate(false)} onCreated={(gid) => {
          setShowCreate(false);
          loadGroups().then(() => {
            // Auto-åpne nylig opprettet gruppe
            api.get("/group-conversations").then((r) => {
              const ng = r.data.find((g) => g.id === gid);
              if (ng) openGroup(ng);
            });
          });
        }} />
      )}
    </div>
  );
}

function CreateGroupModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [usernamesInput, setUsernamesInput] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    const list = usernamesInput.split(/[,\s]+/).map((u) => u.trim()).filter(Boolean);
    if (name.trim().length < 2) return toast.error("Gruppenavn må være minst 2 tegn");
    if (list.length < 2) return toast.error("Du må invitere minst 2 personer");
    if (list.length > 10) return toast.error("Maks 10 deltakere (+ deg = 11)");
    setBusy(true);
    try {
      const r = await api.post("/group-conversations", {
        name: name.trim(),
        participant_usernames: list,
      });
      toast.success(`Gruppe «${name}» opprettet med ${r.data.participants} medlemmer`);
      onCreated(r.data.group_id);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div className="relative w-full max-w-md mx-4 bg-[var(--noir-elev)] border border-white/10 p-7" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-[var(--cream-soft)] hover:text-[var(--cream)]">
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 flex items-center justify-center bg-[var(--magenta)]/15 border border-[var(--magenta)]/40">
            <Users className="h-5 w-5 text-[var(--magenta)]" />
          </div>
          <div>
            <div className="font-fraunces italic text-2xl text-[var(--cream)] leading-none">Ny gruppe</div>
            <div className="font-meta mt-1">Krever VIP-medlemskap</div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <div className="font-meta mb-1.5">Gruppenavn (2–50 tegn)</div>
            <input value={name} onChange={(e) => setName(e.target.value)}
              maxLength={50}
              placeholder="f.eks. Sommerklubben"
              className="input-editorial w-full"
              data-testid="group-name-input" />
          </div>
          <div>
            <div className="font-meta mb-1.5">Brukernavn (komma-separert, 2–10 personer)</div>
            <textarea value={usernamesInput} onChange={(e) => setUsernamesInput(e.target.value)}
              placeholder="Maja, Henrik, Sofie"
              rows={3}
              className="input-editorial w-full resize-none"
              data-testid="group-usernames-input" />
            <div className="font-meta mt-1.5 text-[10px] text-[var(--cream-faint)]">
              Mottakere må ha aktivert «Tillat gruppe-DM» i sine innstillinger.
            </div>
          </div>
        </div>

        <button onClick={create} disabled={busy} className="btn-magenta w-full py-2.5 mt-5"
          data-testid="group-create-submit">
          {busy ? "Oppretter…" : "Opprett gruppe"}
        </button>
      </div>
    </div>
  );
}
